import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldAlert, Lock } from "lucide-react";

const STAFF_ROLES = ["provider_admin", "platform_admin", "tutor"];
const inputCls = "w-full px-4 py-3 bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:border-[var(--accent)] outline-none text-sm";

export default function StaffLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (!STAFF_ROLES.includes(u.role)) {
        await logout();
        setError("This portal is for providers and administrators only. Learners should use the main login.");
        return;
      }
      toast.success(`Welcome, ${u.name.split(" ")[0]}`);
      navigate("/provider");
    } catch (err) {
      if (err.response) {
        setError(formatApiErrorDetail(err.response.data?.detail) || `Login failed (${err.response.status}).`);
      } else {
        setError("Cannot reach the server. Please check your connection and try again.");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] bg-[var(--ink)] grain flex items-center justify-center px-4 py-16" data-testid="staff-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid place-items-center h-9 w-9 bg-[var(--accent)] text-white"><Lock size={18} /></span>
          <span className="font-display font-extrabold text-white tracking-tight">STAFF &amp; ADMIN PORTAL</span>
        </div>

        <div className="border border-[#E65100] bg-[#E65100]/10 text-[#FFB68A] text-sm px-4 py-3 mb-6 flex items-start gap-2" data-testid="restriction-warning">
          <ShieldAlert size={18} className="shrink-0 mt-0.5" />
          <span><strong>Restricted area.</strong> Authorised providers, tutors and administrators only. Unauthorised access attempts are logged and prohibited.</span>
        </div>

        <h1 className="font-display font-extrabold text-3xl tracking-tight text-white mb-1">Staff log in</h1>
        <p className="text-white/50 text-sm mb-7">Manage courses, learners, payments and reports.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="staff-login-form">
          <input data-testid="staff-email" type="email" required placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <input data-testid="staff-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          {error && <p className="text-sm text-[#FF8A65]" data-testid="staff-login-error">{error}</p>}
          <button disabled={loading} data-testid="staff-login-submit" className="w-full bg-[var(--accent)] text-white font-semibold py-3.5 hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-60">{loading ? "Signing in…" : "Sign in"}</button>
        </form>

        <p className="mt-6 text-sm text-white/50">Are you a learner? <Link to="/login" className="text-white font-semibold hover:text-[var(--accent)]" data-testid="staff-to-learner">Use the learner login</Link></p>
      </div>
    </div>
  );
}
